/**
 * Admin → Notifications
 *
 * Compose, review, and retract in-app notifications.
 *
 * Deleting a broadcast removes it from every dashboard it reached (the
 * fan-out rows cascade), so the delete confirmation names the blast radius
 * explicitly rather than asking a generic "are you sure?".
 *
 * Reference: Notification-System.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bell,
  Plus,
  RefreshCw,
  Trash2,
  Search,
  X,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { AlertModal } from '../components/AlertModal';
import { ConfirmModal } from '../components/ConfirmModal';

const API = import.meta.env.VITE_API_URL;

type Severity = 'info' | 'warning' | 'critical';
type Audience = 'all' | 'selected';

interface Broadcast {
  id: string;
  title: string;
  body: string;
  severity: Severity;
  link_url: string | null;
  link_label: string | null;
  audience: Audience;
  exclude_internal: boolean;
  recipient_count: number;
  starts_at: string;
  expires_at: string | null;
  created_at: string;
  delivered_count: number;
  read_count: number;
  is_expired: boolean;
  is_scheduled: boolean;
}

interface PickerLocation {
  location_id: string;
  billing_display_name: string | null;
  is_internal: boolean;
}

/** Raw broadcast row from the detail endpoint (no computed list fields). */
interface BroadcastDetail {
  id: string;
  title: string;
  body: string;
  severity: Severity;
  link_url: string | null;
  link_label: string | null;
  audience: Audience;
  exclude_internal: boolean;
  recipient_count: number;
  starts_at: string;
  expires_at: string | null;
  created_at: string;
}

interface RecipientRow {
  id: string;
  location_id: string;
  is_read: boolean;
  read_at: string | null;
  billing_display_name: string | null;
}

const MAX_TITLE = 200;
const MAX_BODY = 5000;
const PAGE_SIZE = 50;

const SEVERITY_META: Record<Severity, { label: string; dot: string; chip: string; Icon: typeof Bell }> = {
  info: {
    label: 'Info',
    dot: 'bg-indigo-600',
    chip: 'bg-indigo-100 text-indigo-800',
    Icon: Bell,
  },
  warning: {
    label: 'Warning',
    dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-800',
    Icon: AlertTriangle,
  },
  critical: {
    label: 'Critical',
    dot: 'bg-red-600',
    chip: 'bg-red-100 text-red-800',
    Icon: AlertCircle,
  },
};

/** Business name, or an identifiable fallback for a location that has none. */
function locationLabel(loc: PickerLocation): string {
  return loc.billing_display_name || `(unnamed — ${loc.location_id})`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminNotifications() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [severityFilter, setSeverityFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [titleSearch, setTitleSearch] = useState('');

  const [composeOpen, setComposeOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Broadcast | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (severityFilter) params.set('severity', severityFilter);
      if (audienceFilter) params.set('audience', audienceFilter);
      if (activeOnly) params.set('active', 'true');

      const res = await fetch(`${API}/admin/notifications?${params.toString()}`, {
        credentials: 'include',
      });
      const data = (await res.json()) as {
        success: boolean;
        data?: Broadcast[];
        total?: number;
        error?: string;
      };

      if (!data.success) {
        setError(data.error || 'Failed to load notifications');
        return;
      }
      setBroadcasts(data.data || []);
      setTotal(data.total || 0);
    } catch {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [page, severityFilter, audienceFilter, activeOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  // Title search is client-side over the current page — the list is small and
  // this avoids a round trip on every keystroke.
  const visible = useMemo(() => {
    const q = titleSearch.trim().toLowerCase();
    if (!q) return broadcasts;
    return broadcasts.filter((b) => b.title.toLowerCase().includes(q));
  }, [broadcasts, titleSearch]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/admin/notifications/${pendingDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = (await res.json()) as { success: boolean; error?: string };

      if (!data.success) {
        setError(data.error || 'Failed to delete notification');
        return;
      }
      setSuccess('Notification deleted and removed from all accounts.');
      setPendingDelete(null);
      await load();
    } catch {
      setError('Failed to delete notification');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <AlertModal isOpen={!!error} title="Error" message={error || ''} onClose={() => setError(null)} />
      <AlertModal
        isOpen={!!success}
        title="Success"
        variant="success"
        message={success || ''}
        onClose={() => setSuccess(null)}
      />

      <ConfirmModal
        isOpen={!!pendingDelete}
        danger
        title="Delete notification?"
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        message={
          pendingDelete
            ? `"${pendingDelete.title}"\n\n` +
              `This removes the notification from all ${pendingDelete.delivered_count} account(s) it was sent to, ` +
              `including the ${pendingDelete.read_count} that have already read it. It will disappear from their notification history.\n\n` +
              `This cannot be undone.`
            : ''
        }
        onConfirm={() => void handleDelete()}
        onCancel={() => setPendingDelete(null)}
      />

      {detailId && (
        <DetailModal
          broadcastId={detailId}
          onClose={() => setDetailId(null)}
          onError={(msg) => setError(msg)}
        />
      )}

      {composeOpen && (
        <ComposeModal
          onClose={() => setComposeOpen(false)}
          onSent={(count) => {
            setComposeOpen(false);
            setSuccess(`Notification sent to ${count} account${count === 1 ? '' : 's'}.`);
            setPage(0);
            void load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
          <p className="text-sm text-gray-600 mt-1">
            In-app messages shown on customer dashboards.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setComposeOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span className="text-sm">New Notification</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-700">Severity:</span>
          <select
            value={severityFilter}
            onChange={(e) => {
              setSeverityFilter(e.target.value);
              setPage(0);
            }}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">All</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-700">Audience:</span>
          <select
            value={audienceFilter}
            onChange={(e) => {
              setAudienceFilter(e.target.value);
              setPage(0);
            }}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">All</option>
            <option value="all">All accounts</option>
            <option value="selected">Specific accounts</option>
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(0);
            }}
            className="rounded border-gray-300"
          />
          Active only
        </label>

        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={titleSearch}
            onChange={(e) => setTitleSearch(e.target.value)}
            placeholder="Search title"
            className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm w-56"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Audience</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Read</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No notifications found</p>
                  </td>
                </tr>
              )}

              {visible.map((b) => {
                const meta = SEVERITY_META[b.severity] || SEVERITY_META.info;
                const pct =
                  b.delivered_count > 0
                    ? Math.round((b.read_count / b.delivered_count) * 100)
                    : 0;

                return (
                  <tr
                    key={b.id}
                    onClick={() => setDetailId(b.id)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${
                      b.is_expired ? 'bg-gray-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${meta.chip}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                        {meta.label}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className={`text-sm font-medium ${b.is_expired ? 'text-gray-500' : 'text-gray-900'}`}>
                        {b.title}
                        {b.is_expired && (
                          <span className="ml-2 text-xs italic text-gray-400">Expired</span>
                        )}
                        {b.is_scheduled && (
                          <span className="ml-2 text-xs italic text-blue-500">Scheduled</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {b.is_scheduled
                          ? `Starts ${formatDate(b.starts_at)}`
                          : b.expires_at
                            ? `Expires ${formatDate(b.expires_at)}`
                            : 'No expiry'}
                        {b.link_url && (
                          <a
                            href={b.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          >
                            link <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {b.audience === 'all' ? (
                        <>
                          <div>All ({b.recipient_count})</div>
                          {b.exclude_internal && (
                            <div className="text-xs text-gray-500">excl. internal</div>
                          )}
                        </>
                      ) : (
                        <div>
                          {b.recipient_count} account{b.recipient_count === 1 ? '' : 's'}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(b.created_at)}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      <div>
                        {b.read_count}/{b.delivered_count}
                      </div>
                      <div className="text-xs text-gray-500">{pct}%</div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(b);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
            >
              ‹ Prev
            </button>
            <span className="text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
              disabled={page + 1 >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Detail modal
// ============================================================================

const RECIPIENTS_PAGE = 100;

/**
 * Full text of a sent broadcast plus who has read it.
 *
 * This is the only place an admin can read back the exact body a customer
 * received — the list view shows the title only.
 */
function DetailModal({
  broadcastId,
  onClose,
  onError,
}: {
  broadcastId: string;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const [broadcast, setBroadcast] = useState<BroadcastDetail | null>(null);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [recipientPage, setRecipientPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const params = new URLSearchParams({
          limit: String(RECIPIENTS_PAGE),
          offset: String(recipientPage * RECIPIENTS_PAGE),
        });
        const res = await fetch(
          `${API}/admin/notifications/${broadcastId}?${params.toString()}`,
          { credentials: 'include' }
        );
        const data = (await res.json()) as {
          success: boolean;
          data?: {
            broadcast: BroadcastDetail;
            recipients: RecipientRow[];
            recipients_total: number;
          };
          error?: string;
        };

        if (cancelled) return;

        if (!data.success || !data.data) {
          onError(data.error || 'Failed to load notification');
          onClose();
          return;
        }

        setBroadcast(data.data.broadcast);
        setRecipients(data.data.recipients);
        setRecipientsTotal(data.data.recipients_total);
      } catch {
        if (!cancelled) {
          onError('Failed to load notification');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // onClose/onError are stable enough for this modal's lifetime; re-running
    // on their identity would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastId, recipientPage]);

  const readCount = recipients.filter((r) => r.is_read).length;
  const totalPages = Math.max(1, Math.ceil(recipientsTotal / RECIPIENTS_PAGE));
  const meta = broadcast ? SEVERITY_META[broadcast.severity] || SEVERITY_META.info : null;
  const MetaIcon = meta?.Icon || Bell;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between gap-3 p-4 border-b border-gray-200">
          <div className="flex items-start gap-2 min-w-0">
            <MetaIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900 break-words">
              {broadcast?.title || 'Notification'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loading && !broadcast && (
            <p className="py-12 text-center text-sm text-gray-500">Loading…</p>
          )}

          {broadcast && (
            <>
              {/* Metadata */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Severity</div>
                  <div className="mt-0.5">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${meta!.chip}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta!.dot}`} />
                      {meta!.label}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Audience</div>
                  <div className="mt-0.5 text-gray-900">
                    {broadcast.audience === 'all'
                      ? `All (${broadcast.recipient_count})`
                      : `${broadcast.recipient_count} account${broadcast.recipient_count === 1 ? '' : 's'}`}
                    {broadcast.audience === 'all' && broadcast.exclude_internal && (
                      <div className="text-xs text-gray-500">excl. internal</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Sent</div>
                  <div className="mt-0.5 text-gray-900">{formatDateTime(broadcast.created_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Window</div>
                  <div className="mt-0.5 text-gray-900">
                    {formatDate(broadcast.starts_at)} →{' '}
                    {broadcast.expires_at ? formatDate(broadcast.expires_at) : 'never'}
                  </div>
                </div>
              </div>

              {/* Message as the customer sees it */}
              <div>
                <div className="text-xs text-gray-500 uppercase mb-1">Message</div>
                <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  {/* Same rendering the customer gets: plain text, line breaks
                      preserved, never HTML. */}
                  <div className="text-sm text-gray-800 whitespace-pre-line break-words">
                    {broadcast.body}
                  </div>
                  {broadcast.link_url && (
                    <a
                      href={broadcast.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      {broadcast.link_label || 'Learn more'}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Per-location read breakdown */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-500 uppercase">
                    Recipients ({recipientsTotal})
                  </div>
                  <div className="text-xs text-gray-500">
                    {readCount} of {recipients.length} read on this page · unread listed first
                  </div>
                </div>

                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Account
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Read at
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {recipients.length === 0 && !loading && (
                        <tr>
                          <td colSpan={3} className="px-3 py-6 text-center text-sm text-gray-500">
                            No recipients
                          </td>
                        </tr>
                      )}
                      {recipients.map((r) => (
                        <tr key={r.id}>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {r.billing_display_name || (
                              <span className="text-gray-500">
                                (unnamed — {r.location_id})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {r.is_read ? (
                              <span className="text-gray-500">Read</span>
                            ) : (
                              <span className="font-medium text-indigo-700">Unread</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {r.read_at ? formatDateTime(r.read_at) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {recipientsTotal > RECIPIENTS_PAGE && (
                  <div className="flex items-center justify-end gap-3 mt-2 text-sm">
                    <button
                      onClick={() => setRecipientPage((p) => Math.max(0, p - 1))}
                      disabled={recipientPage === 0 || loading}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      ‹ Prev
                    </button>
                    <span className="text-gray-600">
                      Page {recipientPage + 1} of {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setRecipientPage((p) => (p + 1 < totalPages ? p + 1 : p))
                      }
                      disabled={recipientPage + 1 >= totalPages || loading}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Next ›
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Compose modal
// ============================================================================

function ComposeModal({
  onClose,
  onSent,
  onError,
}: {
  onClose: () => void;
  onSent: (recipientCount: number) => void;
  onError: (message: string) => void;
}) {
  const [severity, setSeverity] = useState<Severity>('info');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [audience, setAudience] = useState<Audience>('all');
  const [excludeInternal, setExcludeInternal] = useState(true);
  const [startsAt, setStartsAt] = useState('');
  const [sendImmediately, setSendImmediately] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [neverExpires, setNeverExpires] = useState(true);

  const [locations, setLocations] = useState<PickerLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [locationSearch, setLocationSearch] = useState('');

  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API}/admin/notifications/locations`, {
          credentials: 'include',
        });
        const data = (await res.json()) as { success: boolean; data?: PickerLocation[] };
        if (!cancelled && data.success) {
          setLocations(data.data || []);
        }
      } catch {
        /* the count line degrades to 0; sending still validates server-side */
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredLocations = useMemo(() => {
    const q = locationSearch.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(
      (l) =>
        (l.billing_display_name || '').toLowerCase().includes(q) ||
        l.location_id.toLowerCase().includes(q)
    );
  }, [locations, locationSearch]);

  /**
   * Recipient count from the picker feed fetched when this modal opened.
   * Deliberately not re-verified at send time — the fan-out runs inside the
   * RPC against live data, so only this label can be momentarily stale, and
   * the response reports the true count.
   */
  const recipientCount = useMemo(() => {
    if (audience === 'selected') return selectedIds.size;
    return excludeInternal ? locations.filter((l) => !l.is_internal).length : locations.length;
  }, [audience, selectedIds, excludeInternal, locations]);

  const titleTooLong = title.length > MAX_TITLE;
  const bodyTooLong = body.length > MAX_BODY;

  // Both date inputs are plain YYYY-MM-DD, so they resolve to midnight UTC.
  const startIso = !sendImmediately && startsAt ? new Date(startsAt).toISOString() : null;
  const expiryIso = !neverExpires && expiresAt ? new Date(expiresAt).toISOString() : null;
  const windowInvalid =
    expiryIso !== null &&
    new Date(expiryIso).getTime() <= new Date(startIso ?? new Date().toISOString()).getTime();

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !titleTooLong &&
    !bodyTooLong &&
    !windowInvalid &&
    (audience === 'all' || selectedIds.size > 0) &&
    (!linkUrl.trim() || linkLabel.trim().length > 0) &&
    !sending;

  const toggleLocation = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          severity,
          audience,
          location_ids: audience === 'selected' ? Array.from(selectedIds) : [],
          exclude_internal: audience === 'all' ? excludeInternal : false,
          link_url: linkUrl.trim() || null,
          link_label: linkUrl.trim() ? linkLabel.trim() : null,
          starts_at: startIso,
          expires_at: expiryIso,
        }),
      });

      const data = (await res.json()) as {
        success: boolean;
        recipient_count?: number;
        error?: string;
      };

      if (!data.success) {
        onError(data.error || 'Failed to send notification');
        return;
      }
      onSent(data.recipient_count ?? 0);
    } catch {
      onError('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const previewMeta = SEVERITY_META[severity];
  const PreviewIcon = previewMeta.Icon;
  const previewStrip =
    severity === 'info'
      ? 'bg-indigo-50 border border-indigo-300 text-indigo-900'
      : severity === 'warning'
        ? 'bg-amber-50 border border-amber-400 text-amber-900'
        : 'bg-red-50 border border-red-300 text-red-900';
  const previewIconColor =
    severity === 'info' ? 'text-indigo-600' : severity === 'warning' ? 'text-amber-600' : 'text-red-600';
  const previewButton =
    severity === 'info'
      ? 'bg-indigo-600 text-white'
      : severity === 'warning'
        ? 'bg-amber-600 text-white'
        : 'bg-red-600 text-white';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">New Notification</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Severity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
            <div className="flex items-center gap-4">
              {(['info', 'warning', 'critical'] as Severity[]).map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="severity"
                    checked={severity === s}
                    onChange={() => setSeverity(s)}
                  />
                  {SEVERITY_META[s].label}
                </label>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Pricing update effective October 1"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <div className={`text-xs mt-1 text-right ${titleTooLong ? 'text-red-600' : 'text-gray-400'}`}>
              {title.length} / {MAX_TITLE}
            </div>
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              placeholder="Starting October 1, …"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Plain text only. Line breaks are preserved. Do not name outside vendors — say “we” or “OAK”.
            </p>
            <div className={`text-xs mt-1 text-right ${bodyTooLong ? 'text-red-600' : 'text-gray-400'}`}>
              {body.length} / {MAX_BODY}
            </div>
          </div>

          {/* Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://oasisautokonnect.com/pricing"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2"
            />
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="Button label, e.g. View pricing"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            {linkUrl.trim() && !linkLabel.trim() && (
              <p className="text-xs text-red-600 mt-1">A button label is required when a link is set.</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Must be an https:// address.</p>
          </div>

          <hr className="border-gray-200" />

          {/* Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Send to</label>
            <div className="flex items-center gap-6 mb-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="audience"
                  checked={audience === 'all'}
                  onChange={() => setAudience('all')}
                />
                All accounts
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="audience"
                  checked={audience === 'selected'}
                  onChange={() => setAudience('selected')}
                />
                Specific accounts
              </label>
            </div>

            {audience === 'all' && (
              <label className="flex items-center gap-2 text-sm text-gray-700 ml-6">
                <input
                  type="checkbox"
                  checked={excludeInternal}
                  onChange={(e) => setExcludeInternal(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Exclude internal accounts
              </label>
            )}

            {audience === 'selected' && (
              <div className="ml-6">
                <div className="relative mb-2">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    placeholder="Search accounts"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md divide-y divide-gray-100">
                  {locationsLoading && (
                    <div className="px-3 py-6 text-center text-sm text-gray-500">Loading accounts…</div>
                  )}
                  {!locationsLoading && filteredLocations.length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-gray-500">No accounts match</div>
                  )}
                  {filteredLocations.map((loc) => (
                    <label
                      key={loc.location_id}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(loc.location_id)}
                        onChange={() => toggleLocation(loc.location_id)}
                        className="rounded border-gray-300"
                      />
                      <span className="flex-1 truncate">{locationLabel(loc)}</span>
                      {loc.is_internal && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">
                          internal
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-gray-600">{selectedIds.size} selected</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedIds(new Set())}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedIds(new Set(filteredLocations.map((l) => l.location_id)))
                      }
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Select all ({filteredLocations.length})
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-700 mt-3 ml-6">
              → <span className="font-semibold">{recipientCount}</span> account
              {recipientCount === 1 ? '' : 's'} will receive this
            </p>
          </div>

          <hr className="border-gray-200" />

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Show from</label>
            <div className="flex items-center gap-4">
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                disabled={sendImmediately}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sendImmediately}
                  onChange={(e) => setSendImmediately(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Immediately
              </label>
            </div>
            {!sendImmediately && startsAt && (
              <p className="text-xs text-gray-500 mt-1">
                Recipients are locked in now — accounts created after today will not receive
                this, even though it stays hidden until {formatDate(startIso)}.
              </p>
            )}
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Expires</label>
            <div className="flex items-center gap-4">
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={neverExpires}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={neverExpires}
                  onChange={(e) => setNeverExpires(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Never expires
              </label>
            </div>
            {windowInvalid && (
              <p className="text-xs text-red-600 mt-1">
                Expiry must be after the start date.
              </p>
            )}
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className={`rounded text-sm py-2.5 px-4 ${previewStrip}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <PreviewIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${previewIconColor}`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{title || 'Notification title'}</div>
                    {/* Mirrors NotificationBanners exactly: two-line clamp,
                        newlines collapsed. If that component's teaser changes,
                        change this too or the preview starts lying. */}
                    <div className="mt-0.5 text-xs opacity-80 line-clamp-2 break-words">
                      {body || 'Message preview appears here.'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {linkUrl.trim() && (
                    <span className={`px-3 py-1 text-xs font-semibold rounded ${previewButton}`}>
                      {linkLabel.trim() || 'Learn more'}
                    </span>
                  )}
                  <X className="w-4 h-4 opacity-50" />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              The banner shows the first two lines. Customers see the full message when they click it.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!canSend}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {sending
              ? 'Sending…'
              : `Send to ${recipientCount} account${recipientCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
